import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  adjustAllColors,
  Adjustment,
  NO_ADJUSTMENT,
} from "../../libs/simple/main";
import InputButton from "../InputButton";
import type { PreviewSession } from "./usePreviewSession";

type GlobalAdjustProps = {
  session: PreviewSession;
};

const SLIDERS: { key: keyof Adjustment; label: string; min: number; max: number }[] = [
  { key: "hue", label: "Hue", min: -180, max: 180 },
  { key: "saturation", label: "Colour strength", min: -100, max: 100 },
  { key: "lightness", label: "Brightness", min: -50, max: 50 },
];

const isZero = (value: Adjustment) =>
  value.hue === 0 && value.saturation === 0 && value.lightness === 0;

// "+40", "0", "−20" (a real minus sign), hue in degrees.
function formatValue(key: keyof Adjustment, value: number): string {
  const sign = value > 0 ? "+" : value < 0 ? "\u2212" : "";
  return `${sign}${Math.abs(value)}${key === "hue" ? "\u00b0" : ""}`;
}

// Shifts every colour of the map at once. The sliders preview; Apply makes
// the result one undo step, Reset or leaving the overview puts the map back.
export default function GlobalAdjust(props: GlobalAdjustProps) {
  const { t } = useTranslation();
  const { session } = props;
  const [moved, setMoved] = useState<Adjustment>(NO_ADJUSTMENT);
  const dirty = useRef(false);

  // The sliders only mean something while their preview is on the map. When
  // the session ended from outside (an undo while a slider was moved), they
  // read zero again and there is nothing left to apply or reset.
  const value = session.isPreviewing() ? moved : NO_ADJUSTMENT;

  const preview = (next: Adjustment) => {
    setMoved(next);
    // Sliders dragged back to zero are no change at all, not an undo step.
    if (isZero(next)) {
      dirty.current = false;
      session.cancel();
      return;
    }
    dirty.current = true;
    session.preview((base) => adjustAllColors(base, next));
  };

  const finish = (how: "commit" | "cancel") => {
    dirty.current = false;
    setMoved(NO_ADJUSTMENT);
    session[how]();
  };

  // Only Apply saves. Leaving with a preview showing (a component or the AI
  // view opened, Simple mode left) puts the map back.
  useEffect(() => {
    return () => {
      if (dirty.current) session.cancel();
    };
  }, [session]);

  return (
    <details className="studio-adjust" data-wd-key="simple:adjust">
      <summary className="studio-adjust__summary">{t("Adjust whole map")}</summary>
      {SLIDERS.map((slider) => (
        <label className="studio-adjust__row" key={slider.key}>
          <span>{t(slider.label)}</span>
          <output
            className="studio-adjust__value"
            dir="ltr"
            data-wd-key={`simple:adjust:${slider.key}:value`}
          >
            {formatValue(slider.key, value[slider.key])}
          </output>
          <input
            type="range"
            min={slider.min}
            max={slider.max}
            value={value[slider.key]}
            data-wd-key={`simple:adjust:${slider.key}`}
            onChange={(e) =>
              preview({ ...value, [slider.key]: Number(e.target.value) })
            }
          />
        </label>
      ))}
      <div className="studio-adjust__actions">
        <InputButton
          className="studio-button--tinted"
          data-wd-key="simple:adjust:apply"
          disabled={isZero(value)}
          onClick={() => finish("commit")}
        >
          {t("Apply")}
        </InputButton>
        <InputButton
          className="studio-button--quiet"
          data-wd-key="simple:adjust:grey"
          onClick={() => preview({ ...value, saturation: -100 })}
        >
          {t("Black & white")}
        </InputButton>
        <InputButton
          className="studio-button--quiet"
          data-wd-key="simple:adjust:reset"
          disabled={isZero(value)}
          onClick={() => finish("cancel")}
        >
          {t("Reset")}
        </InputButton>
      </div>
    </details>
  );
}
